import { useCallback, useEffect, useState } from 'react';
import { Activity, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Combobox, EmptyState, Input, MultiSelect, Skeleton, Textarea, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { ImageUpload } from '../components/ImageUpload';
import { EQUIPMENT, LEVELS } from '../lib/programOptions';
import {
  createExercise,
  deleteExercise,
  getExerciseFilters,
  searchAdminExercises,
  updateExercise,
  type AdminExercise,
} from '../lib/adminApi';

const MUSCLE_GROUPS = ['Chest', 'Back', 'Shoulders', 'Arms', 'Legs', 'Core'];

const filterSelect =
  'rounded-control border border-line bg-elevated px-3 py-2 text-sm text-ink outline-none focus:border-ink-soft';

export function ExercisesPage() {
  const [q, setQ] = useState('');
  const [muscle, setMuscle] = useState('');
  const [equipment, setEquipment] = useState('');
  const [list, setList] = useState<AdminExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<{ equipment: string[]; categories: string[] } | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    getExerciseFilters().then(setFilters).catch(() => setFilters(null));
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    return searchAdminExercises({ q: q.trim() || undefined, muscleGroup: muscle || undefined, equipment: equipment || undefined })
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [q, muscle, equipment]);

  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
  }, [reload]);

  if (adding) {
    return (
      <FormShell title="New exercise" onBack={() => setAdding(false)}>
        <ExerciseForm mode="create" onCancel={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />
      </FormShell>
    );
  }

  const editing = editingId ? list.find((e) => e.id === editingId) : undefined;
  if (editing) {
    return (
      <FormShell title={`Edit · ${editing.name}`} onBack={() => setEditingId(null)}>
        <ExerciseForm mode="edit" exercise={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  async function remove(e: AdminExercise) {
    const ok = await confirm({ title: `Delete ${e.name}?`, description: 'It will be removed from the library. Existing programs that used it keep their other exercises.', confirmLabel: 'Delete', tone: 'danger' });
    if (!ok) return;
    await deleteExercise(e.id);
    reload();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-display-sm">Exercises</h1>
          <p className="text-sm text-muted">The shared library used to build every program.</p>
        </div>
        <Button onClick={() => setAdding(true)}><Plus className="size-4" aria-hidden /> New exercise</Button>
      </div>

      <Card className="flex flex-col gap-3 p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search exercises…"
            className="w-full rounded-control border border-line bg-elevated py-2.5 pl-9 pr-3 text-sm text-ink outline-none focus:border-ink-soft"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select className={filterSelect} value={muscle} onChange={(e) => setMuscle(e.target.value)}>
            <option value="">Any muscle</option>
            {MUSCLE_GROUPS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className={filterSelect} value={equipment} onChange={(e) => setEquipment(e.target.value)}>
            <option value="">Any equipment</option>
            {(filters?.equipment ?? []).map((eq) => <option key={eq} value={eq}>{eq}</option>)}
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : list.length === 0 ? (
        <EmptyState icon={<Activity />} title="No exercises found" description="Try a different search, or add a new exercise to the library." />
      ) : (
        <div className="flex flex-col gap-2">
          {list.map((e) => (
            <Card key={e.id} className="flex items-center gap-3 p-3">
              {e.image ? <img src={e.image} alt="" className="size-12 shrink-0 rounded-control object-cover" /> : <span className="grid size-12 shrink-0 place-items-center rounded-control bg-surface text-muted"><Activity className="size-5" aria-hidden /></span>}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{e.name}</p>
                <p className="truncate text-xs capitalize text-muted">
                  {[e.equipment, e.muscleGroups.join(', '), e.custom ? 'Custom' : null].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button type="button" aria-label="Edit" onClick={() => setEditingId(e.id)} className="grid size-9 place-items-center rounded-control text-muted hover:bg-surface hover:text-ink">
                <Pencil className="size-4" aria-hidden />
              </button>
              <button type="button" aria-label="Delete" onClick={() => remove(e)} className="grid size-9 place-items-center rounded-control text-muted hover:bg-surface hover:text-bad-ink">
                <Trash2 className="size-4" aria-hidden />
              </button>
            </Card>
          ))}
          {list.length >= 100 && <p className="px-1 text-xs text-muted">Showing the first 100 — narrow your search to see more.</p>}
        </div>
      )}
    </div>
  );
}

function ExerciseForm({ mode, exercise, onCancel, onDone }: { mode: 'create' | 'edit'; exercise?: AdminExercise; onCancel: () => void; onDone: () => void }) {
  const [name, setName] = useState(exercise?.name ?? '');
  const [category, setCategory] = useState(exercise?.category ?? '');
  const [equipment, setEquipment] = useState(exercise?.equipment ?? '');
  const [level, setLevel] = useState(exercise?.level ?? '');
  const [muscleGroups, setMuscleGroups] = useState<string[]>(exercise?.muscleGroups ?? []);
  const [primaryMuscles, setPrimaryMuscles] = useState<string[]>(exercise?.primaryMuscles ?? []);
  const [instructions, setInstructions] = useState((exercise?.instructions ?? []).join('\n'));
  const [image, setImage] = useState(exercise?.image ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!name.trim()) return setError('Give the exercise a name.');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        category: category.trim() || null,
        equipment: equipment.trim() || null,
        level: level.trim() || null,
        muscleGroups,
        primaryMuscles,
        instructions: instructions.split('\n').map((s) => s.trim()).filter(Boolean),
        image: image.trim() || null,
      };
      if (mode === 'create') await createExercise(body);
      else await updateExercise(exercise!.id, body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the exercise.');
      setSaving(false);
    }
  }

  return (
    <Card className="mb-1 flex flex-col gap-3 p-5">
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Goblet squat" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Combobox label="Equipment" value={equipment} onChange={setEquipment} options={EQUIPMENT} placeholder="Pick equipment" />
        <Combobox label="Level" value={level} onChange={setLevel} options={LEVELS} placeholder="Pick a level" />
      </div>
      <MultiSelect label="Muscle groups" values={muscleGroups} onChange={setMuscleGroups} options={MUSCLE_GROUPS} allowCustom={false} placeholder="Which areas it trains" />
      <MultiSelect label="Primary muscles" values={primaryMuscles} onChange={setPrimaryMuscles} options={['chest', 'lats', 'shoulders', 'biceps', 'triceps', 'quadriceps', 'hamstrings', 'glutes', 'calves', 'abdominals']} placeholder="Add specific muscles" />
      <Combobox label="Type" value={category} onChange={setCategory} options={['strength', 'stretching', 'plyometrics', 'cardio', 'powerlifting', 'olympic weightlifting', 'strongman']} placeholder="Pick a type" />
      <ImageUpload label="Image" value={image || null} onChange={(url) => setImage(url ?? '')} aspect={4 / 3} />
      <Textarea label="Instructions" hint="One step per line" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={6} />
      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}
